import { IdentityResult } from './scoring'

interface ShareData {
  firstName: string
  lastName: string
  bruteScore: number
  nerdTimeSeconds: number
  identity: IdentityResult
}

export async function generateShareImage(data: ShareData): Promise<Blob> {
  const canvas = document.createElement('canvas')
  canvas.width = 1200
  canvas.height = 630
  const ctx = canvas.getContext('2d')!

  // Background
  ctx.fillStyle = '#09090b'
  ctx.fillRect(0, 0, 1200, 630)

  // Orange left glow
  const orangeGrad = ctx.createRadialGradient(0, 315, 0, 0, 315, 500)
  orangeGrad.addColorStop(0, 'rgba(249,115,22,0.15)')
  orangeGrad.addColorStop(1, 'transparent')
  ctx.fillStyle = orangeGrad
  ctx.fillRect(0, 0, 1200, 630)

  // Cyan right glow
  const cyanGrad = ctx.createRadialGradient(1200, 315, 0, 1200, 315, 500)
  cyanGrad.addColorStop(0, 'rgba(6,182,212,0.15)')
  cyanGrad.addColorStop(1, 'transparent')
  ctx.fillStyle = cyanGrad
  ctx.fillRect(0, 0, 1200, 630)

  // Title: BRUTES VS NERDS
  ctx.font = 'bold 52px Impact, sans-serif'
  ctx.fillStyle = '#f97316'
  ctx.textAlign = 'left'
  ctx.fillText('BRUTES', 80, 90)
  ctx.fillStyle = '#ffffff'
  ctx.fillText(' VS ', 80 + ctx.measureText('BRUTES').width, 90)
  ctx.fillStyle = '#22d3ee'
  const vsWidth = ctx.measureText('BRUTES VS ').width
  ctx.font = 'bold 52px "Courier New", monospace'
  ctx.fillText('NERDS', 80 + vsWidth, 90)

  // Subtitle
  ctx.font = '18px Arial, sans-serif'
  ctx.fillStyle = '#71717a'
  ctx.textAlign = 'left'
  ctx.fillText('THE ULTIMATE PODCAST SHOWDOWN', 80, 120)

  // Divider
  ctx.strokeStyle = '#27272a'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(80, 140)
  ctx.lineTo(1120, 140)
  ctx.stroke()

  // Player name
  ctx.font = 'bold 64px Impact, sans-serif'
  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'left'
  ctx.fillText(`${data.firstName} ${data.lastName}`.toUpperCase(), 80, 220)

  // Identity label
  ctx.font = 'bold 36px Arial, sans-serif'
  ctx.fillStyle = data.identity.brutePercent >= 50 ? '#f97316' : '#22d3ee'
  ctx.fillText(data.identity.label, 80, 270)

  // Description
  ctx.font = '20px Arial, sans-serif'
  ctx.fillStyle = '#a1a1aa'
  ctx.fillText(data.identity.description, 80, 310)

  // Split bar
  const barX = 80,
    barY = 340,
    barW = 700,
    barH = 36
  const bruteW = (data.identity.brutePercent / 100) * barW

  // Bar background
  ctx.fillStyle = '#27272a'
  ctx.beginPath()
  ctx.roundRect(barX, barY, barW, barH, 18)
  ctx.fill()

  // Brute fill
  if (bruteW > 0) {
    const bruteGrad = ctx.createLinearGradient(barX, 0, barX + bruteW, 0)
    bruteGrad.addColorStop(0, '#ea580c')
    bruteGrad.addColorStop(1, '#f97316')
    ctx.fillStyle = bruteGrad
    ctx.beginPath()
    ctx.roundRect(barX, barY, bruteW, barH, 18)
    ctx.fill()
  }

  // Nerd fill (right side)
  const nerdW = barW - bruteW
  if (nerdW > 0) {
    const nerdGrad = ctx.createLinearGradient(barX + bruteW, 0, barX + barW, 0)
    nerdGrad.addColorStop(0, '#0891b2')
    nerdGrad.addColorStop(1, '#06b6d4')
    ctx.fillStyle = nerdGrad
    ctx.beginPath()
    ctx.roundRect(barX + bruteW, barY, nerdW, barH, 18)
    ctx.fill()
  }

  // Bar labels
  ctx.font = 'bold 14px Arial, sans-serif'
  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'left'
  ctx.fillText(`${data.identity.brutePercent}% BRUTE`, barX + 12, barY + 24)
  ctx.textAlign = 'right'
  ctx.fillText(
    `${data.identity.nerdPercent}% NERD`,
    barX + barW - 12,
    barY + 24,
  )

  // Score boxes
  const scoreY = 420

  // Brute score box
  ctx.fillStyle = 'rgba(249,115,22,0.1)'
  ctx.strokeStyle = 'rgba(249,115,22,0.4)'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.roundRect(80, scoreY, 300, 120, 12)
  ctx.fill()
  ctx.stroke()

  ctx.font = 'bold 16px Arial, sans-serif'
  ctx.fillStyle = '#f97316'
  ctx.textAlign = 'center'
  ctx.fillText('BRUTE SCORE', 230, scoreY + 30)
  ctx.font = 'bold 52px Impact, sans-serif'
  ctx.fillStyle = '#ffffff'
  ctx.fillText(`${data.bruteScore} lbs`, 230, scoreY + 90)

  // Nerd score box
  ctx.fillStyle = 'rgba(6,182,212,0.1)'
  ctx.strokeStyle = 'rgba(6,182,212,0.4)'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.roundRect(420, scoreY, 300, 120, 12)
  ctx.fill()
  ctx.stroke()

  ctx.font = 'bold 16px Arial, sans-serif'
  ctx.fillStyle = '#22d3ee'
  ctx.textAlign = 'center'
  ctx.fillText('NERD SCORE', 570, scoreY + 30)
  ctx.font = 'bold 52px "Courier New", monospace'
  ctx.fillStyle = '#ffffff'
  ctx.fillText(`${data.nerdTimeSeconds.toFixed(1)}s`, 570, scoreY + 90)

  // LetLois branding
  ctx.fillStyle = '#3f3f46'
  ctx.fillRect(0, 590, 1200, 40)
  ctx.font = 'bold 16px Arial, sans-serif'
  ctx.fillStyle = '#71717a'
  ctx.textAlign = 'center'
  ctx.fillText('Powered by LetLois LLC  •  letlois.com', 600, 615)

  return new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob!), 'image/png'),
  )
}

export async function shareResults(data: ShareData): Promise<void> {
  const blob = await generateShareImage(data)
  const file = new File([blob], 'brutes-vs-nerds-result.png', {
    type: 'image/png',
  })

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({
      title: 'Brutes vs Nerds — My Results',
      text: `I scored ${data.bruteScore} lbs on the Brute test and ${data.nerdTimeSeconds.toFixed(1)}s on the Nerd test! I'm ${data.identity.brutePercent}% Brute. Can you beat me?`,
      files: [file],
    })
  } else {
    // Fallback: download
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'brutes-vs-nerds-result.png'
    a.click()
    URL.revokeObjectURL(url)
  }
}
